import { useEffect } from "react";

type FunctionProps = {
  videoRef: React.RefObject<HTMLVideoElement>;
  startTime: number | undefined;
  setIsReady: (ready: boolean) => void;
  src: string | undefined;
  play: () => void;
};

export function usePlayAtStartTime({
  videoRef,
  startTime,
  setIsReady,
  src,
  play,
}: FunctionProps) {
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Hide until we seek to the requested start time to avoid the first-frame flash
    setIsReady(startTime === 0);

    const onLoadedMetadata = () => {
      if (startTime && startTime > 0) {
        // Set the time after metadata is loaded so browsers don't flash frame 0
        try {
          video.currentTime = startTime;
        } catch {
          // Some browsers require a small defer before seeking
          setTimeout(() => {
            try {
              video.currentTime = startTime;
            } catch {
              /* ignore seek errors */
            }
          }, 0);
        }
      }
    };

    const onSeeked = () => {
      setIsReady(true);
      play();
    };

    const onCanPlay = () => {
      // If no seek is required, play as soon as we can
      if (!startTime || startTime === 0) {
        play();
      }
    };

    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("seeked", onSeeked);
    video.addEventListener("canplay", onCanPlay);

    // If the `src` changes, reset visibility until the new start time is applied
    return () => {
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("canplay", onCanPlay);
    };
  }, [startTime, src, play, setIsReady, videoRef]);
}
